import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { Check, ChevronDown, ChevronRight, Globe, MapPin, Minus, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { countriesQueryOptions, countryLocationsQueryOptions } from '@/lib/query-options';
import { formatLocationName } from '@/lib/utils';
import type { Location } from '@/lib/types';

/**
 * Shared "Countries & Regions" location picker (#4497, #4642). Browses the
 * cached geo catalogue A–Z, drills arbitrarily deep into a country's
 * regions/cities, supports multi-select, select-all and one-click expand
 * (#4629). Controlled via `selected` / `onChange` so it can back both the
 * Business "Add location" dialog and the Campaign targeting selector.
 */

/** Small brand-coloured checkbox. */
function CheckBox({
  checked,
  indeterminate = false,
  onClick,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onClick: () => void;
  label: string;
}) {
  const filled = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={label}
      onClick={onClick}
      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
        filled
          ? 'border-[#290BD9] bg-[#290BD9] text-white'
          : 'border-gray-300 bg-white hover:border-gray-400'
      }`}
    >
      {indeterminate ? (
        <Minus className="h-3 w-3" strokeWidth={3} />
      ) : (
        checked && <Check className="h-3 w-3" strokeWidth={3} />
      )}
    </button>
  );
}

/** Pluralise a uniform set of target types for the "Select all N …" label. */
function pluralizeType(items: Location[]): string {
  const types = new Set(items.map((i) => i.target_type?.toLowerCase()).filter(Boolean));
  if (types.size !== 1) return 'locations';
  const t = [...types][0] as string;
  return t.endsWith('y') ? `${t.slice(0, -1)}ies` : `${t}s`;
}

/** One-click expand bar: select/deselect every child shown under a node (#4629). */
function ChildSelectAll({
  items,
  indent,
  selectedIds,
  onToggleMany,
}: {
  items: Location[];
  indent: string;
  selectedIds: Set<number>;
  onToggleMany: (items: Location[], select: boolean) => void;
}) {
  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.criteria_id));
  const someSelected = items.some((i) => selectedIds.has(i.criteria_id));
  return (
    <div className="flex items-center gap-2 py-1" style={{ paddingLeft: indent }}>
      <CheckBox
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onClick={() => onToggleMany(items, !allSelected)}
        label={allSelected ? 'Deselect all' : 'Select all'}
      />
      <button
        type="button"
        onClick={() => onToggleMany(items, !allSelected)}
        className="text-xs font-medium text-[#290BD9] hover:underline"
      >
        {allSelected ? 'Deselect all' : `Select all ${items.length} ${pluralizeType(items)}`}
      </button>
    </div>
  );
}

/**
 * Recursive node in the country drill-down. Each node is checkable (adds that
 * node as one target). Nodes flagged `has_children` expand to lazily fetch and
 * render their own children — tunnel Country -> County -> City -> Neighborhood.
 */
function LocationNode({
  node,
  countryCode,
  depth,
  selectedIds,
  onToggle,
  onToggleMany,
}: {
  node: Location;
  countryCode: string;
  depth: number;
  selectedIds: Set<number>;
  onToggle: (loc: Location) => void;
  onToggleMany: (items: Location[], select: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const children = useQuery(
    countryLocationsQueryOptions(expanded ? countryCode : null, node.criteria_id, ''),
  );
  const childResults = children.data ?? [];
  const checked = selectedIds.has(node.criteria_id);
  const childIndent = `${(depth + 1) * 16 + 8}px`;

  return (
    <div>
      <div
        className="flex items-center gap-2 rounded py-1.5 pr-1 hover:bg-white"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <CheckBox checked={checked} onClick={() => onToggle(node)} label={`Add ${node.name}`} />
        <button
          type="button"
          onClick={() => onToggle(node)}
          className="flex min-w-0 flex-1 items-center text-left"
        >
          <span className="min-w-0 truncate text-sm text-gray-700">
            {formatLocationName(node.name)}
          </span>
        </button>
        <span className="flex-shrink-0 text-xs capitalize text-gray-400">
          {node.target_type?.toLowerCase()}
        </span>
        {node.has_children ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
            className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-5 flex-shrink-0" />
        )}
      </div>
      {expanded &&
        (children.isLoading ? (
          <p className="py-1 text-xs text-gray-500" style={{ paddingLeft: childIndent }}>
            Loading...
          </p>
        ) : childResults.length === 0 ? (
          <p className="py-1 text-xs text-gray-500" style={{ paddingLeft: childIndent }}>
            No further breakdown.
          </p>
        ) : (
          <>
            <ChildSelectAll
              items={childResults}
              indent={childIndent}
              selectedIds={selectedIds}
              onToggleMany={onToggleMany}
            />
            {childResults.map((child) => (
              <LocationNode
                key={child.criteria_id}
                node={child}
                countryCode={countryCode}
                depth={depth + 1}
                selectedIds={selectedIds}
                onToggle={onToggle}
                onToggleMany={onToggleMany}
              />
            ))}
          </>
        ))}
    </div>
  );
}

/**
 * One row in the A–Z country list. The country itself is checkable (adds the
 * whole country as one target). Expanding reveals a search scoped to that
 * country — its regions/counties by default, or a name search across the
 * country once the user types. Each result drills down via LocationNode.
 */
function CountryRow({
  country,
  isSelected,
  selectedIds,
  onToggle,
  onToggleMany,
}: {
  country: Location;
  isSelected: boolean;
  selectedIds: Set<number>;
  onToggle: (loc: Location) => void;
  onToggleMany: (items: Location[], select: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [subQuery, setSubQuery] = useState('');
  const [debouncedSub] = useDebounce(subQuery, 300);

  const subs = useQuery(
    countryLocationsQueryOptions(
      expanded ? country.country_code : null,
      country.criteria_id,
      debouncedSub,
    ),
  );
  const subResults = subs.data ?? [];

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
        <CheckBox
          checked={isSelected}
          onClick={() => onToggle(country)}
          label={`Add ${country.name}`}
        />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-center justify-between text-left"
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm text-gray-900">{country.name}</span>
            <span className="text-xs uppercase text-gray-400">{country.country_code}</span>
          </span>
          {expanded ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
          )}
        </button>
      </div>

      {expanded && (
        <div className="bg-gray-50 px-3 pb-3 pl-10 pt-1">
          <div className="relative mb-2 mt-3">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              value={subQuery}
              onChange={(e) => setSubQuery(e.target.value)}
              placeholder={`Search regions, cities or airports in ${country.name}...`}
              className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-sm text-gray-900 focus:border-[#290BD9] focus:outline-none"
            />
          </div>
          {subs.isLoading ? (
            <p className="px-1 py-1 text-xs text-gray-500">Loading...</p>
          ) : subResults.length === 0 ? (
            <p className="px-1 py-1 text-xs text-gray-500">
              {debouncedSub.trim() ? 'No matches.' : 'No regions found.'}
            </p>
          ) : (
            <div className="max-h-[240px] overflow-y-auto">
              <ChildSelectAll
                items={subResults}
                indent="8px"
                selectedIds={selectedIds}
                onToggleMany={onToggleMany}
              />
              <div className="space-y-0.5">
                {subResults.map((loc) => (
                  <LocationNode
                    key={loc.criteria_id}
                    node={loc}
                    countryCode={country.country_code}
                    depth={0}
                    selectedIds={selectedIds}
                    onToggle={onToggle}
                    onToggleMany={onToggleMany}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export interface CountryRegionPickerProps {
  selected: Location[];
  onChange: (next: Location[]) => void;
  /**
   * Business-only: when provided, a "switch to Worldwide" nudge appears once
   * every country is selected unfiltered (selecting all 200+ countries as
   * separate targets is rarely what's intended). Omit for campaign targeting,
   * which has no global concept.
   */
  onTargetWorldwide?: () => void;
  /**
   * Max-height utility class for the country list (default `max-h-[340px]`).
   * Pass a shorter value in dense layouts like the campaign-creation wizard.
   */
  listMaxHeightClass?: string;
}

export function CountryRegionPicker({
  selected,
  onChange,
  onTargetWorldwide,
  listMaxHeightClass = 'max-h-[340px]',
}: CountryRegionPickerProps) {
  const [countryFilter, setCountryFilter] = useState('');
  const countries = useQuery(countriesQueryOptions());

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.criteria_id)), [selected]);

  const filteredCountries = useMemo(() => {
    const list = countries.data ?? [];
    const f = countryFilter.trim().toLowerCase();
    if (!f) return list;
    return list.filter((c) => c.name.toLowerCase().includes(f));
  }, [countries.data, countryFilter]);

  const allFilteredSelected =
    filteredCountries.length > 0 && filteredCountries.every((c) => selectedIds.has(c.criteria_id));
  const someFilteredSelected = filteredCountries.some((c) => selectedIds.has(c.criteria_id));

  const toggleSelect = (loc: Location) => {
    onChange(
      selectedIds.has(loc.criteria_id)
        ? selected.filter((s) => s.criteria_id !== loc.criteria_id)
        : [...selected, loc],
    );
  };

  const toggleMany = (items: Location[], select: boolean) => {
    if (select) {
      const existing = new Set(selected.map((s) => s.criteria_id));
      onChange([...selected, ...items.filter((i) => !existing.has(i.criteria_id))]);
    } else {
      const ids = new Set(items.map((i) => i.criteria_id));
      onChange(selected.filter((s) => !ids.has(s.criteria_id)));
    }
  };

  const removeSelected = (criteriaId: number) => {
    onChange(selected.filter((s) => s.criteria_id !== criteriaId));
  };

  return (
    <div className="space-y-3">
      {selected.length > 0 && (
        <div className="flex max-h-[92px] flex-wrap gap-2 overflow-y-auto rounded-lg">
          {selected.map((loc) => (
            <Badge
              key={loc.criteria_id}
              variant="secondary"
              className="flex items-center gap-1 bg-[#EDE9FE] text-[#290BD9] hover:bg-[#EDE9FE]"
            >
              <MapPin className="h-3 w-3" />
              {formatLocationName(loc.canonical_name)}
              <button
                type="button"
                onClick={() => removeSelected(loc.criteria_id)}
                aria-label={`Remove ${loc.canonical_name}`}
                className="ml-1 rounded hover:bg-[#C4B5FD]"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {onTargetWorldwide && !countryFilter.trim() && allFilteredSelected && (
        <div className="flex items-start gap-3 border-l-4 border-[#C4B5FD] bg-[#EDE9FE] p-3 text-sm text-[#290BD9]">
          <Globe className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex-1">
            <p>
              Targeting every country adds {filteredCountries.length} separate locations. To serve
              customers everywhere, a single Worldwide target is cleaner.
            </p>
            <button
              type="button"
              onClick={onTargetWorldwide}
              className="mt-1 font-medium underline hover:text-[#2309C5]"
            >
              Switch to Worldwide
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg border">
        <div className="relative border-b">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            placeholder="Filter countries..."
            className="w-full rounded-t-lg py-2.5 pl-9 pr-3 text-sm text-gray-900 focus:outline-none"
            data-testid="location-picker-country-filter"
          />
        </div>
        {countries.isLoading ? (
          <div className="p-4 text-sm text-gray-500">Loading countries...</div>
        ) : filteredCountries.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No countries match.</div>
        ) : (
          <>
            <div className="flex items-center gap-3 border-b bg-gray-50 px-3 py-2">
              <CheckBox
                checked={allFilteredSelected}
                indeterminate={someFilteredSelected && !allFilteredSelected}
                onClick={() => toggleMany(filteredCountries, !allFilteredSelected)}
                label="Select all countries"
              />
              <button
                type="button"
                onClick={() => toggleMany(filteredCountries, !allFilteredSelected)}
                className="text-sm font-medium text-gray-700"
                data-testid="location-picker-select-all-countries"
              >
                {allFilteredSelected ? 'Deselect all' : 'Select all countries'}
                {countryFilter.trim() ? ` (${filteredCountries.length})` : ''}
              </button>
            </div>
            <div className={`${listMaxHeightClass} overflow-y-auto`}>
              {filteredCountries.map((c) => (
                <CountryRow
                  key={c.criteria_id}
                  country={c}
                  isSelected={selectedIds.has(c.criteria_id)}
                  selectedIds={selectedIds}
                  onToggle={toggleSelect}
                  onToggleMany={toggleMany}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CountryRegionPicker;
